import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from "react";
import mime from "mime";
import TextEditor from "../components/editor";

export const FilesContext = createContext<any>(null);

export function FilesProvider({ children, onChange, files, value = '' }: PropsWithChildren<{ value: string, files: File[], onChange: (text: string, files: File[]) => void }>) {
  const [val, setVal] = useState<string>();

  const started = useRef(false);

  const handleEditorChange = useCallback(
    async (newContent: string) => {
      setVal(newContent)

      const { transformedHtml, newFiles } = await extractImagesAndTransform(newContent, files);

      const changedFiles = await changes(files, newFiles, async (a, b) => await fileToBase64(a) === await fileToBase64(b));

      onChange(transformedHtml, [ ...files, ...changedFiles.added ]);
    },
    [files?.map(i => i.name)]
  );
  
  useEffect(
    () => {
      if (!started.current && files) {
        transform(value, files).then(result => {
          setVal(result);
        });

        started.current = true;
      }
    },
    [value, files]
  );

  return val !== undefined ? (
    <FilesContext.Provider
      value={{
        value: val,
        handleEditorChange,
      }}
    >
      {children}
    </FilesContext.Provider>
  ) : null;
}

export function WYSIWYGEditor({ disabled }: { disabled?: boolean }) {
  const {
    value,
    handleEditorChange
  } = useContext(FilesContext);

  return (
    <>
      <TextEditor
        value={value}
        onChange={handleEditorChange}
        disabled={disabled}
      />
    </>
  );
}

function generateFileName(index = 0) {
  return `attachment_${Date.now()}_${index}`;
}

async function transform(html: string, files: File[]) {
  const imgRegex = /<img [^>]*src="([^"]+)"[^>]*>/g;
  let match;
  let newHtml = html;

  let promises: Promise<void>[] = [];

  while ((match = imgRegex.exec(html)) !== null) {
    let originalSrc = match[1];

    if (!originalSrc.startsWith('cid:')) {
      continue;
    }

    const name = originalSrc.split(':')[1];

    const file = files.find(i => i.name === name);

    if (file) {
      // eslint-disable-next-line no-loop-func
      promises.push(new Promise(async resolve => {
        const base64 = await fileToBase64(file);

        newHtml = newHtml.replace(originalSrc, base64);

        resolve();
      }))
    }
  }

  await Promise.all(promises);

  return newHtml;
}

async function extractImagesAndTransform(html: string, files: File[]) {
  const imgRegex = /<img [^>]*src="([^"]+)"[^>]*>/g;
  let match;
  let newHtml = html;
  let localFiles: File[] = [];

  let index = 0;
  while ((match = imgRegex.exec(html)) !== null) {
    let originalSrc = match[1];

    const filename = generateFileName(index);
    index += 1;

    if (originalSrc.startsWith('cid:')) {
      continue;
    }

    if (!originalSrc.startsWith('data:image')) {
      originalSrc = await blobToBase64(originalSrc);
    }

    const contentType = originalSrc.substring(
      originalSrc.indexOf(':') + 1,
      originalSrc.indexOf(';')
    );

    const existingFile = (await Promise.all(files.map(async i => ({ file: i, base64: await fileToBase64(i) }) ))).find(i => i.base64 === originalSrc);

    localFiles.push(base64ToFile(originalSrc, existingFile ? existingFile.file.name : `${filename}.${mime.getExtension(contentType)}`));

    newHtml = newHtml.replace(originalSrc, existingFile ? `cid:${existingFile.file.name}` : `cid:${filename}.${mime.getExtension(contentType)}`);
  }

  return { transformedHtml: newHtml, newFiles: localFiles };
}

export const base64ToFile = (base64: string, name: string) => {
  const base64Data = base64.split(',')[1];

  const contentType = base64.substring(
    base64.indexOf(':') + 1,
    base64.indexOf(';')
  );

  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const fileBlob = new Blob([byteArray], { type: contentType });

  const file = new File([fileBlob], name, { type: contentType });

  return file;
}

const blobToBase64 = (url: string): Promise<string> => {
  return new Promise(async (resolve, _) => {
    const response = await fetch(url);

    const blob = await response.blob();

    const fileReader = new FileReader();

    fileReader.readAsDataURL(blob);

    fileReader.onloadend = function(){
      if (!fileReader.result) {
        return _();
      }

      resolve(fileReader.result.toString());
    }
  });
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result!.toString());
    reader.onerror = reject;
  });
}

const changes = async <T, >(arr1: T[], arr2: T[], ident: (i: T, j: T) => Promise<boolean> = (i, j) => Promise.resolve(i === j)): Promise<{ added: T[] }> => {
  return {
    added: (await Promise.all(arr2.map(async a2 => ({ a2, isNew: !(await Promise.all(arr1.map(async a1 => ident(a1, a2) ))).find(Boolean) })))).filter(i => i.isNew).map(i => i.a2)
  }
}
